import { useCallback } from 'react';

import type { GetOneInchGasPricesResponse } from '@api';

// NOTE:
// {
//   "baseFee": "43673842272",
//   "low": {
//     "maxPriorityFeePerGas": "29378708",
//     "maxFeePerGas": "52437989434"
//     /* everything else */
//   },
// }
//
// since this gas price is in gwei you can just sum everything,
// the user will need at least the max fee + priority fee,
// so 52467368142 in this case.
// gas * gas price => 190081 * 52467368142 = 9973049803799502.
// ETH is 18 decimals so that number / 10^18 = 0.009973049803799502 ETH
// is how much needs to be subtracted from the user's balance

const useGetOneInchGasPrice = ({ gasPrices }: { gasPrices?: GetOneInchGasPricesResponse }) => {
  const getOneInchGasPrice = useCallback(() => {
    const { standard, low, medium, high, fast, instant } = gasPrices?.attributes || {};

    if (standard) {
      return standard;
    }

    if (low?.maxFeePerGas) {
      const result = BigInt(low.maxFeePerGas) + BigInt(low.maxPriorityFeePerGas || 0);
      return result.toString();
    }

    if (medium?.maxFeePerGas) {
      const result = BigInt(medium.maxFeePerGas) + BigInt(medium.maxPriorityFeePerGas || 0);
      return result.toString();
    }

    if (high?.maxFeePerGas) {
      const result = BigInt(high.maxFeePerGas) + BigInt(high.maxPriorityFeePerGas || 0);
      return result.toString();
    }

    if (instant && typeof instant === 'object' && instant?.maxFeePerGas) {
      const result = BigInt(instant.maxFeePerGas) + BigInt(instant.maxPriorityFeePerGas || 0);
      return result.toString();
    }

    if (instant && typeof instant === 'string') {
      return instant;
    }

    if (fast) {
      return fast;
    }

    return '0';
  }, [gasPrices]);

  return { getOneInchGasPrice };
};

export default useGetOneInchGasPrice;
